import { useState } from 'react';
import { ChevronDown, ChevronRight, Check, AlertCircle, Clock, Loader2 } from 'lucide-react';

export interface PipelineStageData {
  id: string;
  title: string;
  description: string;
  model: string;
  status: 'pending' | 'running' | 'success' | 'error' | 'skipped';
  input?: unknown;
  output?: unknown;
  outputLabel?: string;
  outputIsJson?: boolean;
  metrics?: {
    inputTokens?: number;
    outputTokens?: number;
    durationMs?: number;
    cost?: number;
  };
  promptStats?: {
    promptTokensEstimate?: number;
    promptContextLimit?: number;
    promptContextRatio?: number;
  } | null;
}

interface PipelineStageCardProps {
  stage: PipelineStageData;
  step?: number;
  compact?: boolean;
}

function formatNumber(value: unknown): string {
  return typeof value === 'number' && Number.isFinite(value) ? value.toLocaleString() : '-';
}

function formatDurationMs(value: unknown): string {
  return typeof value === 'number' && Number.isFinite(value) ? `${value}ms` : '-';
}

function formatPercent(value: unknown): string {
  return typeof value === 'number' && Number.isFinite(value) ? `${Math.round(value * 100)}%` : '-';
}

function StatusIcon({ status }: { status: PipelineStageData['status'] }) {
  switch (status) {
    case 'success':
      return <Check size={14} className="text-success" />;
    case 'error':
      return <AlertCircle size={14} className="text-ember" />;
    case 'running':
      return <Loader2 size={14} className="text-rune animate-spin" />;
    case 'skipped':
      return <Clock size={14} className="text-mist/50" />;
    default:
      return <Clock size={14} className="text-mist/30" />;
  }
}

export function PipelineStageCard({ stage, step, compact = false }: PipelineStageCardProps) {
  const [showInput, setShowInput] = useState(false);
  const [showOutput, setShowOutput] = useState(false);

  const hasInput = stage.input !== undefined;
  const hasOutput = stage.output !== undefined;
  const hasMetrics = stage.metrics && Object.keys(stage.metrics).length > 0;

  return (
    <div
      className={`bg-void border rounded-xl overflow-hidden ${
        stage.status === 'error' ? 'border-ember/40' : 'border-stone/30'
      }`}
    >
      <div className="p-4 space-y-3">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <StatusIcon status={stage.status} />
            <div>
              <div className="text-sm font-semibold text-parchment">
                {step !== undefined && <span className="text-mist mr-1">{step}.</span>}
                {stage.title}
              </div>
              {!compact && <div className="text-xs text-mist mt-0.5">{stage.description}</div>}
            </div>
          </div>
          <div className="text-xs text-mist text-right space-y-1">
            <div>
              Model: <span className="text-parchment">{stage.model}</span>
            </div>
            {hasMetrics && stage.metrics && (
              <div className="flex flex-wrap gap-3 justify-end">
                {stage.metrics.inputTokens !== undefined && (
                  <span>
                    In: <span className="text-parchment">{formatNumber(stage.metrics.inputTokens)}</span>
                  </span>
                )}
                {stage.metrics.outputTokens !== undefined && (
                  <span>
                    Out: <span className="text-parchment">{formatNumber(stage.metrics.outputTokens)}</span>
                  </span>
                )}
                {stage.metrics.durationMs !== undefined && (
                  <span>
                    <span className="text-parchment">{formatDurationMs(stage.metrics.durationMs)}</span>
                  </span>
                )}
              </div>
            )}
            {stage.promptStats && (
              <div>
                Prompt:{' '}
                <span className="text-parchment">
                  {formatNumber(stage.promptStats.promptTokensEstimate)}
                </span>
                <span className="mx-1">/</span>
                <span className="text-parchment">
                  {formatNumber(stage.promptStats.promptContextLimit)}
                </span>
                <span className="ml-1">({formatPercent(stage.promptStats.promptContextRatio)})</span>
              </div>
            )}
          </div>
        </div>

        {/* Expandable Input */}
        {hasInput && (
          <div>
            <button
              onClick={() => setShowInput(!showInput)}
              className="flex items-center gap-1 text-xs text-mist hover:text-parchment"
            >
              {showInput ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
              Input
            </button>
            {showInput && (
              <pre className="mt-2 p-3 bg-night/70 rounded-lg text-xs text-mist overflow-auto max-h-64">
                {JSON.stringify(stage.input, null, 2)}
              </pre>
            )}
          </div>
        )}

        {/* Expandable Output */}
        {hasOutput && (
          <div>
            <button
              onClick={() => setShowOutput(!showOutput)}
              className="flex items-center gap-1 text-xs text-mist hover:text-parchment"
            >
              {showOutput ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
              {stage.outputLabel ?? 'Output'}
            </button>
            {showOutput && (
              <pre
                className={`mt-2 p-3 bg-night/70 rounded-lg text-xs text-mist overflow-auto max-h-64 ${
                  stage.outputIsJson === false ? 'whitespace-pre-wrap' : ''
                }`}
              >
                {stage.outputIsJson === false
                  ? String(stage.output)
                  : JSON.stringify(stage.output, null, 2)}
              </pre>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
